import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ShopBottomNav from '../../components/ShopBottomNav';
import { recordRedemption, getTodayStats } from '../../services/shopState';
import { scanByCode, scanQR } from '../../services/apiService';
import './ShopScanPage.css';

function isDemoMode() {
  return sessionStorage.getItem('walkin_demo_mode') === 'true';
}

function readAllDeals() {
  try {
    return JSON.parse(localStorage.getItem('walkin_shop_deals') || '[]');
  } catch {
    return [];
  }
}

function isOwned(deal, shopName) {
  return !!shopName && (deal.shopName === shopName || deal.createdBy === shopName);
}

// Returns { kind: 'owned' | 'foreign' | 'inactive' | 'unknown', deal? }
function lookupCode(code, shopName) {
  const all = readAllDeals();
  const found = all.find((d) => d.code === code);
  if (!found) return { kind: 'unknown' };
  if (!isOwned(found, shopName)) return { kind: 'foreign' };
  if (found.status !== 'actief') return { kind: 'inactive', deal: found };
  return { kind: 'owned', deal: found };
}

const STATES = {
  READY: 'ready',
  VALID: 'valid',
  REDEEMED: 'redeemed',
  INVALID: 'invalid',
  FOREIGN: 'foreign', // valid code but belongs to another shop
};

function nowTime() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const CONFETTI_PIECES = Array.from({ length: 30 });

// html5-qrcode mounts its <video> into an element looked up by id.
const CAMERA_ELEMENT_ID = 'shop-scan-camera';

export default function ShopScanPage() {
  const navigate = useNavigate();
  const [state, setState] = useState(STATES.READY);
  const [code, setCode] = useState('');
  const [matched, setMatched] = useState(null);
  const initialToday = getTodayStats(sessionStorage.getItem('walkin_shop_name'));
  const [redeemedCount, setRedeemedCount] = useState(initialToday.redemptions);

  const [checking, setChecking] = useState(false);

  const checkCode = async (raw) => {
    const trimmed = (raw || '').trim();

    // Demo mode: validate against the locally seeded deals (the backend has no
    // knowledge of demo codes).
    if (isDemoMode()) {
      const shopName = sessionStorage.getItem('walkin_shop_name');
      const result = lookupCode(trimmed, shopName);
      if (result.kind === 'owned') {
        setMatched({ code: trimmed, deal: result.deal.title, dealRecord: result.deal, time: nowTime() });
        setState(STATES.VALID);
      } else if (result.kind === 'foreign') {
        setMatched({ code: trimmed });
        setState(STATES.FOREIGN);
      } else {
        setState(STATES.INVALID);
      }
      return;
    }

    // Live mode: the backend validates the code against this retailer (JWT)
    // and records the redemption in one call.
    setChecking(true);
    try {
      const res = await scanByCode(trimmed);
      setMatched({
        code: trimmed,
        deal: res?.dealTitle || res?.deal?.title || res?.title || 'Deal',
        dealRecord: res,
        time: nowTime(),
        redeemed: true, // already recorded server-side on scan
      });
      setState(STATES.VALID);
    } catch (err) {
      // 403 → code belongs to another retailer; anything else → invalid/expired
      setState(err?.status === 403 ? STATES.FOREIGN : STATES.INVALID);
      if (err?.status === 403) setMatched({ code: trimmed });
    } finally {
      setChecking(false);
    }
  };

  // --- Camera ---------------------------------------------------------
  // Real camera stream in the viewfinder while we are in the READY state.
  // html5-qrcode is imported on demand so its decoder stays out of the main
  // bundle. Every failure path here is swallowed on purpose: no camera,
  // a denied permission or an insecure origin all just leave the viewfinder
  // dark, and the manual code entry below is the fallback.
  const scannedRef = useRef(false);
  const cameraChainRef = useRef(Promise.resolve());

  useEffect(() => {
    if (state !== STATES.READY) return undefined;

    let scanner = null;
    let cancelled = false;
    scannedRef.current = false;

    const stop = async () => {
      const running = scanner;
      scanner = null;
      if (!running) return;
      try {
        if (running.isScanning) await running.stop();
        running.clear();
      } catch {
        // already stopped, or the element is gone — nothing left to do
      }
    };

    // Start and stop are queued on one promise chain. Two Html5Qrcode
    // instances sharing an element wipe each other's <video> out of the DOM,
    // which is exactly what happens when the previous teardown is still in
    // flight — on every READY → result → READY round trip, and twice at mount
    // under StrictMode in development.
    const enqueue = (task) => {
      cameraChainRef.current = cameraChainRef.current.then(task, task);
    };

    enqueue(async () => {
      if (cancelled) return;
      try {
        const { Html5Qrcode, Html5QrcodeSupportedFormats } = await import('html5-qrcode');
        if (cancelled || !document.getElementById(CAMERA_ELEMENT_ID)) return;

        scanner = new Html5Qrcode(CAMERA_ELEMENT_ID, {
          formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
          verbose: false,
        });

        await scanner.start(
          { facingMode: 'environment' },
          {
            fps: 15,
            qrbox: { width: 250, height: 250 },
            // Repeated here because `videoConstraints` replaces the argument
            // above rather than extending it.
            videoConstraints: {
              facingMode: 'environment',
            },
          },
          async (decodedText) => {
            if (scannedRef.current) return; // one hit per scan
            scannedRef.current = true;
            enqueue(stop);

            // The customer's QR image carries the redemption token, not the
            // 6-digit code printed underneath it, and those go to different
            // endpoints: scan-code only ever matches the display code, so
            // handing it a token always came back 'Ongeldige code'.
            if (/^\d{6}$/.test(decodedText)) {
              checkCode(decodedText);
              return;
            }

            try {
              const res = await scanQR(decodedText);
              setMatched({
                code: decodedText,
                deal: res?.dealTitle || res?.deal?.title || res?.title || 'Deal',
                dealRecord: res,
                time: nowTime(),
                redeemed: true, // already recorded server-side on scan
              });
              setState(STATES.VALID);
            } catch (err) {
              setState(err?.status === 403 ? STATES.FOREIGN : STATES.INVALID);
            }
          },
          () => {
            // Fires for every frame without a QR code in it — not an error.
          }
        );
      } catch {
        // Silent by design; see above.
      }
      if (cancelled) await stop();
    });

    return () => {
      cancelled = true;
      enqueue(stop);
    };
    // checkCode only reads session storage and calls setters, so the camera
    // restarts on state changes rather than on every render.
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleManual = (e) => {
    e.preventDefault();
    checkCode(code);
  };

  const handleSimulate = () => {
    const shopName = sessionStorage.getItem('walkin_shop_name');
    const own = readAllDeals().filter(
      (d) => isOwned(d, shopName) && d.status === 'actief' && d.code
    );
    if (own.length === 0) return; // simulate has nothing to fire on
    const pick = own[Math.floor(Math.random() * own.length)];
    setCode(pick.code);
    checkCode(pick.code);
  };

  const hasOwnActive = (() => {
    const shopName = sessionStorage.getItem('walkin_shop_name');
    return readAllDeals().some(
      (d) => isOwned(d, shopName) && d.status === 'actief' && d.code
    );
  })();

  const markRedeemed = () => {
    // In demo mode we record the redemption locally now. In live mode the
    // backend already recorded it at scan time, so we just advance the counter.
    if (isDemoMode() && matched?.dealRecord && !matched.redeemed) {
      recordRedemption({ deal: matched.dealRecord, code: matched.code });
      const shopName = sessionStorage.getItem('walkin_shop_name');
      setRedeemedCount(getTodayStats(shopName).redemptions);
    } else {
      setRedeemedCount((c) => c + 1);
    }
    setState(STATES.REDEEMED);
  };

  const reset = () => {
    setState(STATES.READY);
    setCode('');
    setMatched(null);
  };

  const confetti = useMemo(
    () =>
      CONFETTI_PIECES.map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.6,
        duration: 1.5 + Math.random() * 1.2,
        color: ['#E8621A', '#1A3E5C', '#22C55E', '#0EA5E9', '#F59E0B'][i % 5],
        rotate: Math.random() * 360,
      })),
    [state === STATES.REDEEMED]  // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div className="shop-scan">
      <header className="shop-scan__header">
        <button
          className="shop-scan__back"
          onClick={() => navigate(-1)}
          aria-label="Terug"
        >
          ←
        </button>
        <h1 className="shop-scan__title">Scan QR code</h1>
        <span className="shop-scan__spacer" />
      </header>

      <main className="shop-scan__main">
        {state === STATES.READY && (
          <>
            <div className="shop-scan__viewfinder">
              <div className="shop-scan__camera">
                <div id={CAMERA_ELEMENT_ID} className="shop-scan__camera-mount" />
              </div>
              <div className="shop-scan__corner shop-scan__corner--tl" />
              <div className="shop-scan__corner shop-scan__corner--tr" />
              <div className="shop-scan__corner shop-scan__corner--bl" />
              <div className="shop-scan__corner shop-scan__corner--br" />
              <div className="shop-scan__line" />
              <p className="shop-scan__hint">
                Richt de camera op de QR code van de klant
              </p>
            </div>

            <div className="shop-scan__manual">
              <p className="shop-scan__manual-label">
                Of voer de code handmatig in:
              </p>
              <form onSubmit={handleManual}>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  className="shop-scan__code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                />
                <button
                  type="submit"
                  className="shop-scan__check"
                  disabled={code.length !== 6 || checking}
                >
                  {checking ? 'Controleren…' : 'Controleer code'}
                </button>
              </form>
              <button
                type="button"
                className="shop-scan__simulate"
                onClick={handleSimulate}
                disabled={!hasOwnActive}
                title={hasOwnActive ? '' : 'Geen actieve deals om te simuleren'}
              >
                🎭 Simuleer scan
              </button>

              <p className="shop-scan__counter">
                Vandaag ingeleverd: <strong>{redeemedCount}</strong>
              </p>
            </div>
          </>
        )}

        {state === STATES.VALID && matched && (
          <div className="shop-scan__result shop-scan__result--valid">
            <div className="shop-scan__result-circle shop-scan__result-circle--green">
              ✅
            </div>
            <h2 className="shop-scan__result-title">Deal geldig!</h2>
            <div className="shop-scan__result-deal">{matched.deal}</div>
            <div className="shop-scan__result-meta">
              <div><span>Code:</span><strong>{matched.code}</strong></div>
              <div><span>Tijd:</span><strong>{matched.time}</strong></div>
            </div>
            <button
              className="shop-scan__primary shop-scan__primary--green"
              onClick={markRedeemed}
            >
              Markeer als ingeleverd
            </button>
            <button
              className="shop-scan__cancel"
              onClick={reset}
            >
              Annuleren
            </button>
          </div>
        )}

        {state === STATES.REDEEMED && (
          <div className="shop-scan__result shop-scan__result--redeemed">
            <div className="shop-scan__confetti">
              {confetti.map((c) => (
                <span
                  key={c.id}
                  className="shop-scan__confetti-piece"
                  style={{
                    left: `${c.left}%`,
                    background: c.color,
                    animationDelay: `${c.delay}s`,
                    animationDuration: `${c.duration}s`,
                    transform: `rotate(${c.rotate}deg)`,
                  }}
                />
              ))}
            </div>
            <div className="shop-scan__result-circle shop-scan__result-circle--green">
              ✅
            </div>
            <h2 className="shop-scan__result-title">Deal ingeleverd!</h2>
            <p className="shop-scan__result-sub">Statistieken bijgewerkt</p>
            <div className="shop-scan__counter-update">
              Totaal vandaag: {redeemedCount} {redeemedCount === 1 ? 'inlevering' : 'inleveringen'}
            </div>
            <button
              className="shop-scan__primary"
              onClick={reset}
            >
              Volgende scan
            </button>
          </div>
        )}

        {state === STATES.INVALID && (
          <div className="shop-scan__result shop-scan__result--invalid">
            <div className="shop-scan__result-circle shop-scan__result-circle--red">
              ❌
            </div>
            <h2 className="shop-scan__result-title">Ongeldige code</h2>
            <p className="shop-scan__result-sub">
              Deze code bestaat niet of de deal is niet meer actief.
            </p>
            <button
              className="shop-scan__primary shop-scan__primary--red"
              onClick={reset}
            >
              Probeer opnieuw
            </button>
          </div>
        )}

        {state === STATES.FOREIGN && (
          <div className="shop-scan__result shop-scan__result--invalid">
            <div className="shop-scan__result-circle shop-scan__result-circle--red">
              🚫
            </div>
            <h2 className="shop-scan__result-title">Andere winkel</h2>
            <p className="shop-scan__result-sub">
              Deze code hoort bij een andere winkel.
            </p>
            <button
              className="shop-scan__primary shop-scan__primary--red"
              onClick={reset}
            >
              Probeer opnieuw
            </button>
          </div>
        )}
      </main>

      <ShopBottomNav />
    </div>
  );
}
